import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X, CheckCircle, Clock, UserCircle, ArrowSquareOut, WarningOctagon, Warning, Info, CheckSquare, DotsThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";

/**
 * NotificationBell + NotificationDrawer — Sprint 4 · Priority 6.
 *
 * Consumes /api/activity/summary + /api/activity/actionable so the bell
 * badge and drawer both stay in sync with the shared event model.  All
 * actions (assign / snooze / resolve / dismiss) hit /api/activity/{id}/*.
 *
 * Auto-refresh every 45s, plus a manual Refresh button in the drawer.
 * Deep-links use the `deep_link` field on the event so clicking a row
 * takes the dispatcher to the exact affected record.
 */

const SEVERITY_META = {
  critical: { color: "#DC2626", label: "Critical", Icon: WarningOctagon },
  high:     { color: "#F97316", label: "High",     Icon: Warning },
  medium:   { color: "#EAB308", label: "Medium",   Icon: Bell },
  low:      { color: "#3B82F6", label: "Low",      Icon: Info },
  info:     { color: "#737373", label: "Info",     Icon: Info },
};

const REFRESH_MS = 45_000;
const SNOOZE_OPTIONS = [
  { label: "15 min",  minutes: 15 },
  { label: "1 hour",  minutes: 60 },
  { label: "4 hours", minutes: 240 },
  { label: "Tomorrow", minutes: 60 * 24 },
];

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60)    return `${secs}s ago`;
  if (secs < 3600)  return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen]     = useState(false);
  const [summary, setSummary] = useState({ total_actionable: 0, critical: 0, high: 0, medium: 0, low: 0 });
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [severityFilter, setSeverityFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [snoozeMenuFor, setSnoozeMenuFor] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/activity/summary");
      setSummary(data);
    } catch { /* ignore */ }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (severityFilter) params.set("severity", severityFilter);
      const { data } = await api.get(`/activity/actionable?${params}`);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [severityFilter]);

  // Poll the summary badge on an interval
  useEffect(() => {
    loadSummary();
    const t = setInterval(loadSummary, REFRESH_MS);
    return () => clearInterval(t);
  }, [loadSummary]);

  // Fetch full list when drawer opens or filter changes
  useEffect(() => {
    if (open) loadEvents();
  }, [open, loadEvents]);

  // Escape closes drawer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const doAction = async (event, kind, payload) => {
    setBusyId(event.id);
    try {
      await api.post(`/activity/${event.id}/${kind}`, payload || {});
      toast.success(`Event ${kind}`);
      setSnoozeMenuFor(null);
      await Promise.all([loadEvents(), loadSummary()]);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message || `Failed to ${kind}`);
    } finally {
      setBusyId(null);
    }
  };

  const criticalPulse = summary.critical > 0;
  const badge = summary.total_actionable;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-1.5 text-neutral-400 hover:text-white font-mono text-[10px] uppercase tracking-widest border border-neutral-800 rounded px-2 py-1 transition-colors"
        data-testid="notif-bell"
        title="Notifications"
      >
        <Bell size={12} weight="fill" className={criticalPulse ? "text-red-500" : ""} />
        <span className="hidden md:inline">Alerts</span>
        {badge > 0 && (
          <span
            data-testid="notif-bell-count"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black flex items-center justify-center tabular-nums"
            style={{
              background: criticalPulse ? "#DC2626" : "#F97316",
              color: criticalPulse ? "#fff" : "#0A0A0A",
              border: "1px solid #0A0A0A",
            }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70]"
          data-testid="notif-drawer-overlay"
        >
          <button
            type="button"
            aria-label="Close notifications"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/60"
          />
          <aside
            className="dark-surface absolute top-0 right-0 h-full w-full sm:w-[420px] flex flex-col shadow-2xl border-l border-neutral-800"
            style={{ background: "#0A0A0A", color: "#fff" }}
            data-testid="notif-drawer"
          >
            <header className="flex items-center gap-2 px-4 py-3 border-b border-neutral-800">
              <Bell size={16} weight="fill" style={{ color: "#DC2626" }} />
              <div className="font-display font-black text-lg text-white">Notifications</div>
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={loadEvents}
                  className="rf-btn rf-btn-sm"
                  style={{ background: "#1F1F1F", color: "#F5F5F5", border: "1px solid #404040" }}
                  data-testid="notif-refresh"
                  title="Refresh"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rf-btn rf-btn-sm rf-btn-icon"
                  style={{ background: "#1F1F1F", color: "#F5F5F5", border: "1px solid #404040" }}
                  data-testid="notif-close"
                  aria-label="Close notifications"
                  title="Close"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            </header>

            {/* Severity chips */}
            <div className="flex items-center gap-1.5 px-4 py-2 border-b border-neutral-800 flex-wrap"
                 data-testid="notif-severity-filter">
              <SeverityChip active={severityFilter === ""}         onClick={() => setSeverityFilter("")}         label="All"      count={summary.total_actionable} color="#fff" testid="notif-chip-all" />
              <SeverityChip active={severityFilter === "critical"} onClick={() => setSeverityFilter("critical")} label="Critical" count={summary.critical} color="#DC2626" testid="notif-chip-critical" />
              <SeverityChip active={severityFilter === "high"}     onClick={() => setSeverityFilter("high")}     label="High"     count={summary.high}     color="#F97316" testid="notif-chip-high" />
              <SeverityChip active={severityFilter === "medium"}   onClick={() => setSeverityFilter("medium")}   label="Medium"   count={summary.medium}   color="#EAB308" testid="notif-chip-medium" />
              <SeverityChip active={severityFilter === "low"}      onClick={() => setSeverityFilter("low")}      label="Low"      count={summary.low}      color="#3B82F6" testid="notif-chip-low" />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto" data-testid="notif-list">
              {loading && events.length === 0 && (
                <div className="p-4 text-xs text-neutral-500">Loading notifications…</div>
              )}
              {!loading && events.length === 0 && (
                <div className="p-8 text-center text-sm text-neutral-500" data-testid="notif-empty">
                  <CheckCircle size={40} weight="fill" className="mx-auto mb-2 text-neutral-800" />
                  Nothing to attend to right now.
                </div>
              )}
              {events.map((ev) => {
                const meta = SEVERITY_META[ev.severity] || SEVERITY_META.info;
                const Icon = meta.Icon;
                const isBusy = busyId === ev.id;
                return (
                  <div
                    key={ev.id}
                    className="p-3 border-b border-neutral-900 hover:bg-neutral-900/40 transition-colors"
                    data-testid={`notif-item-${ev.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className="mt-0.5 w-6 h-6 rounded flex items-center justify-center shrink-0"
                        style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
                      >
                        <Icon size={12} weight="fill" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className="label-tag px-1.5 py-0.5 rounded"
                            style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
                          >
                            {ev.severity}
                          </span>
                          <span className="label-tag px-1.5 py-0.5 rounded" style={{ background: "#1F1F1F", color: "#A3A3A3", border: "1px solid #404040" }}>{ev.source}</span>
                          {ev.status === "snoozed" && (
                            <span className="label-tag flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "#78350F", color: "#FDE68A", border: "1px solid #F59E0B" }}><Clock size={10} /> snoozed</span>
                          )}
                          {ev.status === "assigned" && (
                            <span className="label-tag flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "#1E3A8A", color: "#BFDBFE", border: "1px solid #3B82F6" }}><UserCircle size={10} /> {ev.assigned_to_name || "assigned"}</span>
                          )}
                          {ev.status === "open" && (
                            <span className="label-tag flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "#052E1A", color: "#86EFAC", border: "1px solid #16A34A" }}>open</span>
                          )}
                          <span className="ml-auto text-[10px] text-neutral-500 font-mono">{timeAgo(ev.created_at)}</span>
                        </div>
                        <div className="font-bold text-sm text-white mt-1 break-words">{ev.title}</div>
                        {ev.description && (
                          <div className="text-xs text-neutral-400 mt-0.5 break-words">{ev.description}</div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          {ev.deep_link && (
                            <Link
                              to={ev.deep_link}
                              onClick={() => setOpen(false)}
                              className="rf-btn rf-btn-ghost text-[10px]"
                              data-testid={`notif-open-${ev.id}`}
                            >
                              <ArrowSquareOut size={11} weight="bold" /> Open
                            </Link>
                          )}
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => doAction(ev, "resolve")}
                            className="rf-btn rf-btn-ghost text-[10px]"
                            style={{ color: "#22C55E" }}
                            data-testid={`notif-resolve-${ev.id}`}
                          >
                            <CheckSquare size={11} weight="bold" /> Resolve
                          </button>
                          <div className="relative">
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setSnoozeMenuFor(snoozeMenuFor === ev.id ? null : ev.id)}
                              className="rf-btn rf-btn-ghost text-[10px]"
                              style={{ color: "#EAB308" }}
                              data-testid={`notif-snooze-${ev.id}`}
                            >
                              <Clock size={11} weight="bold" /> Snooze
                            </button>
                            {snoozeMenuFor === ev.id && (
                              <div
                                className="absolute right-0 top-full mt-1 z-10 min-w-[140px] rounded border border-neutral-800 shadow-xl p-1"
                                style={{ background: "#0A0A0A" }}
                                data-testid={`notif-snooze-menu-${ev.id}`}
                              >
                                {SNOOZE_OPTIONS.map((opt) => (
                                  <button
                                    key={opt.minutes}
                                    type="button"
                                    onClick={() => doAction(ev, "snooze", { minutes: opt.minutes })}
                                    className="w-full text-left px-2 py-1 text-xs text-neutral-200 hover:bg-neutral-900 rounded"
                                    data-testid={`notif-snooze-${opt.minutes}-${ev.id}`}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => doAction(ev, "assign", { user_id: "me", user_name: "Me" })}
                            className="rf-btn rf-btn-ghost text-[10px]"
                            style={{ color: "#3B82F6" }}
                            data-testid={`notif-assign-${ev.id}`}
                          >
                            <UserCircle size={11} weight="bold" /> Assign me
                          </button>
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => doAction(ev, "dismiss")}
                            className="rf-btn rf-btn-ghost text-[10px] ml-auto"
                            style={{ color: "#737373" }}
                            data-testid={`notif-dismiss-${ev.id}`}
                            title="Dismiss"
                          >
                            <DotsThree size={11} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <footer className="px-4 py-2 border-t border-neutral-800 flex items-center gap-2">
              <Link
                to="/admin/activity"
                onClick={() => setOpen(false)}
                className="text-xs text-neutral-400 hover:text-white"
                data-testid="notif-view-timeline"
              >
                View full activity timeline →
              </Link>
              <span className="ml-auto text-[10px] text-neutral-600 font-mono">Auto-refresh · 45s</span>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
}

function SeverityChip({ active, onClick, label, count, color, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded transition-colors"
      style={{
        background: active ? color : "transparent",
        color: active ? "#000" : color,
        border: `1px solid ${color}55`,
      }}
    >
      {label} {count != null && <span className="ml-1 opacity-80 tabular-nums">{count}</span>}
    </button>
  );
}
